import { useCallback, useEffect, useRef, useState } from "react";
import { Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { adminApi, errText } from "./adminApi";

const BLANK = { name: "", class_exam: "", subject: "Physics", chapter: "", test_number: "", description: "", num_questions: "", max_marks: "", duration_min: "", test_date: "", price_paise: "", published: false };

export default function AdminSeries() {
  const [series, setSeries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [uploading, setUploading] = useState("");
  const fileRefs = useRef({});

  const load = useCallback(async () => {
    const { data } = await adminApi.get("/admin/test-series");
    setSeries(data.series);
  }, []);
  useEffect(() => { load().catch(() => setError("Could not load test series. Click Refresh.")); }, [load]);

  const openEditor = (s) => {
    setEditing(s?.series_id || "new");
    setForm(s ? { ...BLANK, ...s, num_questions: s.num_questions ?? "", max_marks: s.max_marks ?? "", duration_min: s.duration_min ?? "", price_paise: s.price_paise ?? "" } : BLANK);
    setError("");
  };
  const update = (key, value) => setForm((c) => ({ ...c, [key]: value }));

  const save = async () => {
    setError("");
    const payload = { ...form, num_questions: form.num_questions === "" ? null : Number(form.num_questions), max_marks: form.max_marks === "" ? null : Number(form.max_marks), duration_min: form.duration_min === "" ? null : Number(form.duration_min), price_paise: form.price_paise === "" ? null : Math.round(Number(form.price_paise)) };
    try {
      if (editing === "new") await adminApi.post("/admin/test-series", payload);
      else await adminApi.put(`/admin/test-series/${editing}`, payload);
      setNotice("Test series saved.");
      setEditing(null);
      await load();
    } catch (err) { setError(errText(err)); }
  };

  const upload = async (seriesId, kind, file) => {
    if (!file) return;
    setUploading(`${seriesId}:${kind}`);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await adminApi.post(`/admin/test-series/${seriesId}/file?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNotice(`${kind === "pdf" ? "Full paper" : kind === "sample" ? "Sample preview" : "Solutions"} uploaded.`);
      await load();
    } catch (err) { setError(errText(err)); } finally { setUploading(""); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}" permanently?`)) return;
    setError("");
    try { await adminApi.delete(`/admin/test-series/${s.series_id}`); await load(); }
    catch (err) { setError(errText(err)); }
  };

  const FileBtn = ({ s, kind, label }) => (
    <>
      <input type="file" accept="application/pdf" style={{ display: "none" }} ref={(el) => (fileRefs.current[`${s.series_id}:${kind}`] = el)} onChange={(e) => { upload(s.series_id, kind, e.target.files[0]); e.target.value = ""; }} data-testid={`series-file-${kind}-${s.series_id}`} />
      <button className="admin-btn" disabled={uploading === `${s.series_id}:${kind}`} onClick={() => fileRefs.current[`${s.series_id}:${kind}`]?.click()} data-testid={`series-upload-${kind}-${s.series_id}`}>
        <Upload size={13} /> {uploading === `${s.series_id}:${kind}` ? "Uploading…" : s[`has_${kind}`] ? `${label} ✓ replace` : label}
      </button>
    </>
  );

  return (
    <div data-testid="admin-series">
      <h4 style={{ marginBottom: 4 }}>Test Series Management</h4>
      <span className="lead-sub">Upload your prepared test papers as PDFs. Sample previews are public; full papers and solutions unlock only after verified payment.</span>
      {error && <p className="admin-error" data-testid="series-error" style={{ marginTop: 12 }}>{error}</p>}
      {notice && <p className="tp-success-box" style={{ background: "#123a2e", border: "1px solid #12b76a", color: "#4ade80" }}>{notice}</p>}
      <div className="admin-row" style={{ margin: "14px 0" }}>
        <button className="admin-btn" onClick={() => openEditor(null)} data-testid="series-add"><Plus size={14} /> New test series</button>
      </div>
      {editing && <div className="admin-card" data-testid="series-editor">
        <div className="admin-row" style={{ justifyContent: "space-between" }}><h4>{editing === "new" ? "New test series" : "Edit test series"}</h4><button className="admin-btn" onClick={() => setEditing(null)}><X size={14} /></button></div>
        {error && <p className="admin-error">{error}</p>}
        <div className="admin-row">
          <label className="admin-label" style={{ flex: 2 }}>NAME<input className="admin-input" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="e.g. Current Electricity Test 1" data-testid="series-name" /></label>
          <label className="admin-label" style={{ flex: 1 }}>PRICE (PAISE — BLANK = DEFAULT PACK PRICE)<input className="admin-input" value={form.price_paise} onChange={(e) => update("price_paise", e.target.value)} placeholder="4999 = ₹49.99" data-testid="series-price" /></label>
        </div>
        <div className="admin-row">
          <label className="admin-label">CLASS / EXAM<input className="admin-input" value={form.class_exam} onChange={(e) => update("class_exam", e.target.value)} placeholder="Class 12 / NEET" data-testid="series-classexam" /></label>
          <label className="admin-label">SUBJECT<input className="admin-input" value={form.subject} onChange={(e) => update("subject", e.target.value)} data-testid="series-subject" /></label>
          <label className="admin-label">CHAPTER / COVERAGE<input className="admin-input" value={form.chapter} onChange={(e) => update("chapter", e.target.value)} placeholder="Current Electricity" data-testid="series-chapter" /></label>
        </div>
        <div className="admin-row">
          <label className="admin-label">TEST NO.<input className="admin-input" value={form.test_number} onChange={(e) => update("test_number", e.target.value)} data-testid="series-testnum" /></label>
          <label className="admin-label">QUESTIONS<input className="admin-input" value={form.num_questions} onChange={(e) => update("num_questions", e.target.value)} data-testid="series-numq" /></label>
          <label className="admin-label">MAX MARKS<input className="admin-input" value={form.max_marks} onChange={(e) => update("max_marks", e.target.value)} data-testid="series-marks" /></label>
          <label className="admin-label">DURATION (MIN)<input className="admin-input" value={form.duration_min} onChange={(e) => update("duration_min", e.target.value)} data-testid="series-duration" /></label>
          <label className="admin-label">TEST DATE<input className="admin-input" value={form.test_date} onChange={(e) => update("test_date", e.target.value)} placeholder="optional" data-testid="series-date" /></label>
        </div>
        <label className="admin-label">DESCRIPTION<textarea className="admin-textarea" value={form.description} onChange={(e) => update("description", e.target.value)} data-testid="series-desc" /></label>
        <label className="check-row"><input type="checkbox" checked={form.published} onChange={(e) => update("published", e.target.checked)} data-testid="series-published" /> Published (visible in the student store)</label>
        <button className="admin-btn" onClick={save} data-testid="series-save">Save test series</button>
      </div>}
      {series.map((s, i) => <div className="admin-card" key={s.series_id} data-testid={`series-row-${i}`}>
        <div className="admin-row">
          <span className={s.published ? "status-pill approved" : "status-pill locked"}>{s.published ? "PUBLISHED" : "DRAFT"}</span>
          <span style={{ flex: 1, color: "#dbe6f4", fontSize: 13, fontWeight: 600 }}>{s.name} <span className="lead-sub">— {s.chapter || s.class_exam} · ₹{(s.price_paise / 100).toFixed(2)}</span></span>
        </div>
        <div className="admin-row" style={{ marginTop: 10, flexWrap: "wrap" }}>
          <FileBtn s={s} kind="sample" label="Sample PDF" />
          <FileBtn s={s} kind="pdf" label="Full paper PDF" />
          <FileBtn s={s} kind="solutions" label="Solutions PDF" />
          <button className="admin-btn" onClick={() => openEditor(s)} data-testid={`series-edit-${i}`}><Pencil size={13} /> Edit</button>
          <button className="admin-btn" onClick={() => remove(s)} data-testid={`series-delete-${i}`}><Trash2 size={13} /></button>
        </div>
      </div>)}
      {series.length === 0 && <div className="admin-empty">No test series yet. Create one, upload the PDFs, then publish it to the store.</div>}
    </div>
  );
}
